/**
 * ShareChatDialog – modal för att dela en pågående paragraf-chatt.
 * Två alternativ: (1) skapa delbar länk via backend, (2) ladda ner som PDF med jsPDF.
 */
import React, { useState } from "react";
import { X, Link2, FileDown, Check, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { jsPDF } from "jspdf";
import { trackEvent } from "@/lib/analytics";

const SOCIALS = [
  { key: "linkedin", label: "LinkedIn", url: (u) => `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(u)}` },
  { key: "whatsapp", label: "WhatsApp", url: (u, t) => `https://wa.me/?text=${encodeURIComponent(`${t} ${u}`)}` },
  { key: "facebook", label: "Facebook", url: (u) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(u)}` },
  { key: "x", label: "X", url: (u, t) => `https://twitter.com/intent/tweet?url=${encodeURIComponent(u)}&text=${encodeURIComponent(t)}` },
];

export default function ShareChatDialog({ sessionId, sfs, chapter, paragraph, paragraphText, messages, onClose }) {
  const [shareUrl, setShareUrl] = useState(null);
  const [copyingLink, setCopyingLink] = useState(false);
  const [linkLoading, setLinkLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  const createLink = async () => {
    if (linkLoading) return;
    setLinkLoading(true);
    try {
      const { data } = await api.post("/laws/chat/share", { session_id: sessionId });
      const url = `${window.location.origin}/chattdelad/${data.share_token}`;
      setShareUrl(url);
      trackEvent("chatt_delad", { method: "link", paragraph: `${sfs}-${chapter}-${paragraph}` });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunde inte skapa delbar länk.");
    } finally {
      setLinkLoading(false);
    }
  };

  const copyLink = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopyingLink(true);
      setTimeout(() => setCopyingLink(false), 2000);
      toast.success("Länk kopierad!");
    } catch {
      toast.error("Kunde inte kopiera automatiskt – markera och kopiera manuellt.");
    }
  };

  const downloadPdf = () => {
    if (pdfLoading) return;
    setPdfLoading(true);
    try {
      const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const margin = 48;
      const maxW = pageW - margin * 2;
      let y = margin;

      const addLine = (text, opts = {}) => {
        const { size = 11, bold = false, gap = 4, color = [24, 24, 27] } = opts;
        doc.setFontSize(size);
        doc.setFont("helvetica", bold ? "bold" : "normal");
        doc.setTextColor(...color);
        const lines = doc.splitTextToSize(text, maxW);
        for (const line of lines) {
          if (y > pageH - margin) {
            doc.addPage();
            y = margin;
          }
          doc.text(line, margin, y);
          y += size * 1.3;
        }
        y += gap;
      };

      // Header
      addLine("Bygglovskompassen – AI-chatt om lagparagraf", { size: 16, bold: true, gap: 6 });
      addLine(`${sfs} ${chapter} kap. ${paragraph} §`, { size: 12, bold: true, color: [37, 99, 235], gap: 10 });

      // Paragraftext
      if (paragraphText) {
        addLine("Paragrafen:", { size: 10, bold: true, color: [82, 82, 91] });
        addLine(paragraphText, { size: 10, color: [63, 63, 70], gap: 12 });
      }

      // Chat-meddelanden
      addLine("Samtal:", { size: 12, bold: true, gap: 8 });
      for (const m of messages) {
        const isUser = m.role === "user";
        addLine(isUser ? "Du" : "AI-rådgivare", {
          size: 9,
          bold: true,
          color: isUser ? [39, 39, 42] : [37, 99, 235],
          gap: 2,
        });
        addLine(m.text, { size: 11, color: [24, 24, 27], gap: 10 });
      }

      // Footer
      const pageCount = doc.internal.pages.length - 1;
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(161, 161, 170);
        doc.text(
          `Bygglovskompassen.se – hämtad ${new Date().toLocaleDateString("sv-SE")} – sida ${i}/${pageCount}`,
          margin,
          pageH - 20,
        );
        doc.text(
          "AI-svaren är rådgivande – bekräfta med kommunen innan beslut.",
          margin,
          pageH - 8,
        );
      }

      doc.save(`bygglovkollen-chatt-${sfs.replace(":", "-")}-${chapter}-${paragraph}.pdf`);
      toast.success("PDF laddades ner!");

      // GA4-tracking
      if (typeof window !== "undefined" && typeof window.gtag === "function") {
        window.gtag("event", "share_chat", {
          method: "pdf",
          paragraph: `${sfs}-${chapter}-${paragraph}`,
        });
      }
    } catch (e) {
      toast.error("PDF-generering misslyckades.");
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
      data-testid="share-chat-dialog"
    >
      <div
        className="bg-white rounded-sm shadow-2xl max-w-md w-full overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-200">
          <h3 className="text-sm font-semibold text-zinc-900" style={{ fontFamily: "Outfit" }}>
            Dela din chatt om {chapter}:{paragraph} §
          </h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-zinc-100 rounded-sm"
            data-testid="share-chat-close"
            aria-label="Stäng"
          >
            <X className="w-4 h-4 text-zinc-500" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Delbar länk */}
          <div className="border border-zinc-200 rounded-sm p-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center flex-shrink-0">
                <Link2 className="w-4 h-4" />
              </div>
              <div className="flex-1">
                <div className="font-semibold text-sm text-zinc-900 mb-1">Delbar länk</div>
                <p className="text-xs text-zinc-600 leading-relaxed mb-3">
                  Skicka länken till en handläggare, granne eller kollega. De ser samtalet i skrivskyddat läge.
                </p>
                {!shareUrl ? (
                  <button
                    onClick={createLink}
                    disabled={linkLoading}
                    data-testid="share-create-link"
                    className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-sm inline-flex items-center gap-1.5 disabled:opacity-60"
                  >
                    {linkLoading ? (
                      <><Loader2 className="w-3 h-3 animate-spin" /> Skapar länk…</>
                    ) : (
                      "Skapa länk"
                    )}
                  </button>
                ) : (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <input
                        readOnly
                        value={shareUrl}
                        data-testid="share-link-value"
                        className="flex-1 px-2 py-1.5 border border-zinc-300 rounded-sm text-xs bg-zinc-50 font-mono"
                        onClick={(e) => e.target.select()}
                      />
                      <button
                        onClick={copyLink}
                        data-testid="share-copy-link"
                        className="px-2.5 py-1.5 bg-zinc-900 hover:bg-zinc-800 text-white text-xs rounded-sm inline-flex items-center gap-1"
                      >
                        {copyingLink ? <Check className="w-3 h-3" /> : <Link2 className="w-3 h-3" />}
                        {copyingLink ? "Kopierad" : "Kopiera"}
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-1.5 pt-1">
                      {SOCIALS.map((s) => (
                        <button
                          key={s.key}
                          data-testid={`share-social-${s.key}`}
                          onClick={() => {
                            trackEvent("chatt_delad", { method: s.key, paragraph: `${sfs}-${chapter}-${paragraph}` });
                            window.open(s.url(shareUrl, `AI-genomgång av ${sfs} ${chapter} kap. ${paragraph} § – Bygglovskompassen`), "_blank", "noopener");
                          }}
                          className="px-2.5 py-1 text-[11px] font-medium border border-zinc-300 hover:bg-zinc-100 text-zinc-700 rounded-sm transition"
                        >
                          Dela på {s.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* PDF-nedladdning */}
          <div className="border border-zinc-200 rounded-sm p-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0">
                <FileDown className="w-4 h-4" />
              </div>
              <div className="flex-1">
                <div className="font-semibold text-sm text-zinc-900 mb-1">Ladda ner som PDF</div>
                <p className="text-xs text-zinc-600 leading-relaxed mb-3">
                  Sparar hela samtalet inklusive paragraftexten som PDF – enkelt att bifoga till en bygglovsansökan.
                </p>
                <button
                  onClick={downloadPdf}
                  disabled={pdfLoading}
                  data-testid="share-download-pdf"
                  className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium rounded-sm inline-flex items-center gap-1.5 disabled:opacity-60"
                >
                  {pdfLoading ? (
                    <><Loader2 className="w-3 h-3 animate-spin" /> Genererar…</>
                  ) : (
                    <><FileDown className="w-3 h-3" /> Ladda ner PDF</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
